import React, { useState, useEffect } from 'react';
import { resolveImage } from '../../utils/api';

// Resolve bilingual field based on current language
const tField = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field; // backwards compat
  return field[lang] || field.en || '';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-US', options);
};

const tDay = (day, lang) => {
  if (lang !== 'zh') return day;
  const days = { 'Mon':'一', 'Tue':'二', 'Wed':'三', 'Thu':'四', 'Fri':'五', 'Sat':'六', 'Sun':'日' };
  return days[day] || day;
};

const tLevel = (lvl, lang) => {
  if (lang !== 'zh') return lvl;
  const levels = { 'Beginner':'初級', 'Intermediate':'中級', 'Advanced':'高級', 'All Levels':'所有級別' };
  return levels[lvl] || lvl;
};

const FALLBACK_BATCHES = [
  {
    _id: 'fb1',
    name: { en: 'Ballet Foundations — Batch A', zh: '芭蕾基礎班 — A 班' },
    category: { en: 'Ballet', zh: '芭蕾舞' },
    shortDesc: { en: 'Master classical ballet postures, barre exercises, and grace.', zh: '掌握古典芭蕾姿勢、把杆練習和優雅舉止。' },
    startTime: '16:00', endTime: '17:30',
    tutor: { name: 'Ms. Clara Vance', email: 'clara@etsacademy.com' },
    level: 'Beginner',
    days: ['Mon', 'Wed', 'Fri'],
    coverImage: 'https://images.pexels.com/photos/8463000/pexels-photo-8463000.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    _id: 'fb2',
    name: { en: 'Urban Street Hip Hop', zh: '都市街頭嘻哈' },
    category: { en: 'Hip Hop', zh: '嘻哈' },
    shortDesc: { en: 'High-energy choreography, popping, locking, and groove control.', zh: '高能量編舞、彈震、鎖定和節奏控制。' },
    startTime: '18:00', endTime: '19:30',
    tutor: { name: 'Marcus Jay', email: 'marcus@etsacademy.com' },
    level: 'Intermediate',
    days: ['Tue', 'Thu'],
    coverImage: 'https://images.pexels.com/photos/2820884/pexels-photo-2820884.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    _id: 'fb3',
    name: { en: 'Contemporary Flow & Expression', zh: '當代流動與表達' },
    category: { en: 'Contemporary', zh: '當代舞' },
    shortDesc: { en: 'Fluid floorwork, emotional expression, and modern dance technique.', zh: '流暢地板動作、情感表達和現代舞技巧。' },
    startTime: '10:00', endTime: '11:30',
    tutor: { name: 'Elena Rostova', email: 'elena@etsacademy.com' },
    level: 'All Levels',
    days: ['Sat', 'Sun'],
    coverImage: 'https://images.pexels.com/photos/358010/pexels-photo-358010.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    _id: 'fb4',
    name: { en: 'Latin Salsa Sensations', zh: '拉丁薩爾薩感官' },
    category: { en: 'Salsa', zh: '薩爾薩' },
    shortDesc: { en: 'Rhythm, footwork, partner dynamics, and stage presence.', zh: '節奏、步法、舞伴互動和舞台表現。' },
    startTime: '19:00', endTime: '20:30',
    tutor: { name: 'Carlos Mendoza', email: 'carlos@etsacademy.com' },
    level: 'Intermediate',
    days: ['Wed', 'Fri'],
    coverImage: 'https://images.pexels.com/photos/1701202/pexels-photo-1701202.jpeg?auto=compress&cs=tinysrgb&w=600'
  }
];

export default function Classes({ publicBatches = [], scrollTo, lang = 'en' }) {
  const displayBatches = (publicBatches && publicBatches.length > 0) ? publicBatches : FALLBACK_BATCHES;
  const [slideIdx, setSlideIdx] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);

  // Responsive columns calculator
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setVisibleCards(1);
      else if (width < 1024) setVisibleCards(2);
      else setVisibleCards(3);
    };

    handleResize(); // run initially
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIdx = Math.max(0, displayBatches.length - visibleCards);

  // Auto reset slider index if it exceeds maximum allowable index due to resize
  useEffect(() => {
    if (slideIdx > maxIdx) {
      setSlideIdx(maxIdx);
    }
  }, [maxIdx, slideIdx]);

  return (
    <section className="lp-sec lp-sec-alt" id="classes">
      <div className="lp-container">
        <div className="reveal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 20, marginBottom: 52 }}>
          <div>
            <div className="lp-overline">{lang === 'zh' ? '探索舞蹈種類' : 'Explore Dance Styles'}</div>
            <h2 className="lp-sec-title" style={{ marginBottom: 0 }}>
              {lang === 'zh' ? '尋找你的' : 'Find Your'} <em className="lp-text-gold">{lang === 'zh' ? '節奏' : 'Rhythm'}</em>
            </h2>
          </div>
          <p className="lp-sec-sub" style={{ maxWidth: 380, margin: 0 }}>
            {lang === 'zh' ? '每個舞蹈種類均配有認證導師和實時進度追蹤。' : 'Every dance form with certified tutors and real-time progress tracking.'}
          </p>
        </div>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 24, transition: 'transform .4s ease', transform: `translateX(calc(-${slideIdx} * (100% / ${visibleCards} + 24px / ${visibleCards})))` }}>
            {displayBatches.map(b => (
              <div key={b._id} className="lp-class-card" style={{ flex: `0 0 calc(${100 / visibleCards}% - ${24 * (visibleCards - 1) / visibleCards}px)`, minWidth: 280 }}>
                <div style={{ overflow: 'hidden' }}>
                  <img src={resolveImage(b.coverImage) || 'https://images.pexels.com/photos/8463000/pexels-photo-8463000.jpeg?auto=compress&cs=tinysrgb&w=600'} alt={tField(b.name, lang)} className="lp-class-img" />
                </div>
                <div className="lp-class-body">
                  <div className="lp-class-tag">{tField(b.category, lang) || (lang === 'zh' ? '課程' : 'Class')}</div>
                  <div className="lp-class-name">{tField(b.name, lang)}</div>
                  <p className="lp-class-desc">{tField(b.shortDesc, lang) || (lang === 'zh' ? '立即加入這個精彩的課程！' : 'Join this exciting batch today!')}</p>
                  <div className="lp-class-meta">
                    <span style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>⏱ {b.startTime} - {b.endTime}</span>
                    <span style={{ fontSize: 13, display: 'block', marginBottom: b.tutor?.email ? 0 : 4 }}>🎓 {tField(b.tutor?.name, lang) || (lang === 'zh' ? '待定' : 'TBA')}</span>
                    {b.tutor?.email && <span style={{ fontSize: 11, display: 'block', marginBottom: 4, color: '#888', paddingLeft: 18 }}>{b.tutor.email}</span>}
                    {b.startDate && b.endDate && (
                      <span style={{ fontSize: 13, display: 'block', color: 'var(--wine)', fontWeight: 600 }}>📅 {formatDate(b.startDate)} - {formatDate(b.endDate)}</span>
                    )}
                  </div>
                  <div className="lp-class-levels" style={{ marginTop: 12 }}>
                    <span className="lp-class-lv">{tLevel(b.level, lang)}</span>
                    {b.days?.map(d => (
                      <span key={d} className="lp-class-lv" style={{ background: '#f0f0f0', color: '#666', fontWeight: 500 }}>
                        {lang === 'zh' ? `週${tDay(d, lang)}` : d}
                      </span>
                    ))}
                  </div>
                  <button className="lp-class-cta" onClick={() => scrollTo('contact')}>{lang === 'zh' ? '立即查詢' : 'Enquire Now'}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-swiper-nav">
          <button className="lp-swiper-btn" onClick={() => setSlideIdx(p => Math.max(0, p - 1))} disabled={slideIdx === 0} style={{ opacity: slideIdx === 0 ? .4 : 1 }}>←</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {Array.from({ length: maxIdx + 1 }).map((_, i) => (
              <div key={i} onClick={() => setSlideIdx(i)} style={{ width: i === slideIdx ? 24 : 8, height: 8, background: i === slideIdx ? '#7b1d3a' : '#e0e0e0', cursor: 'pointer', transition: 'all .3s' }} />
            ))}
          </div>
          <button className="lp-swiper-btn" onClick={() => setSlideIdx(p => Math.min(maxIdx, p + 1))} disabled={slideIdx === maxIdx} style={{ opacity: slideIdx === maxIdx ? .4 : 1 }}>→</button>
        </div>
      </div>
    </section>
  );
}
